import { useState, useEffect, Key } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  BedDouble,
  Bath,
  Ruler,
  Filter,
  Calendar,
  ArrowUpDown,
  X,
  Check,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Mock data for properties
const properties = [
  {
    id: "1",
    title: "Modern Downtown Apartment",
    description:
      "Luxurious apartment in the heart of downtown with stunning city views and modern amenities.",
    location: "Downtown, New York",
    price: 2500,
    image: "/placeholder.svg?height=400&width=600",
    type: "apartment",
    beds: 2,
    baths: 2,
    sqft: 1200,
    amenities: ["Gym", "Pool", "Parking", "Elevator", "Doorman", "Balcony"],
    available: true,
    availableFrom: "2023-06-15",
    featured: true,
    coordinates: { lat: 40.7128, lng: -74.006 },
    petFriendly: true,
    furnished: true,
    airConditioning: true,
    laundry: "In-unit",
    parking: "Garage",
    yearBuilt: 2018,
  },
  {
    id: "2",
    title: "Cozy Studio with City View",
    description:
      "Charming studio apartment with panoramic city views, perfect for young professionals.",
    location: "Midtown, Chicago",
    price: 1800,
    image: "/placeholder.svg?height=400&width=600",
    type: "studio",
    beds: 1,
    baths: 1,
    sqft: 650,
    amenities: ["Gym", "Rooftop", "Elevator", "Doorman"],
    available: true,
    availableFrom: "2023-05-01",
    featured: false,
    coordinates: { lat: 41.8781, lng: -87.6298 },
    petFriendly: false,
    furnished: false,
    airConditioning: true,
    laundry: "In-building",
    parking: "Street",
    yearBuilt: 2015,
  },
  {
    id: "3",
    title: "Spacious Family House",
    description:
      "Beautiful family home with a large backyard, perfect for families looking for space and comfort.",
    location: "Suburbs, Los Angeles",
    price: 3500,
    image: "/placeholder.svg?height=400&width=600",
    type: "house",
    beds: 4,
    baths: 3,
    sqft: 2400,
    amenities: ["Garden", "Garage", "Fireplace", "Basement", "Patio"],
    available: true,
    availableFrom: "2023-07-01",
    featured: true,
    coordinates: { lat: 34.0522, lng: -118.2437 },
    petFriendly: true,
    furnished: false,
    airConditioning: true,
    laundry: "In-unit",
    parking: "Garage",
    yearBuilt: 2005,
  },
  {
    id: "4",
    title: "Luxury Penthouse",
    description:
      "Exclusive penthouse with panoramic views, private terrace, and premium finishes throughout.",
    location: "Financial District, San Francisco",
    price: 5000,
    image: "/placeholder.svg?height=400&width=600",
    type: "penthouse",
    beds: 3,
    baths: 2,
    sqft: 1800,
    amenities: [
      "Gym",
      "Pool",
      "Concierge",
      "Elevator",
      "Doorman",
      "Terrace",
      "Sauna",
    ],
    available: false,
    availableFrom: "2023-08-15",
    featured: true,
    coordinates: { lat: 37.7749, lng: -122.4194 },
    petFriendly: false,
    furnished: true,
    airConditioning: true,
    laundry: "In-unit",
    parking: "Valet",
    yearBuilt: 2020,
  },
  {
    id: "5",
    title: "Charming Townhouse",
    description:
      "Beautiful townhouse with classic architecture and modern updates in a historic neighborhood.",
    location: "Historic District, Boston",
    price: 3200,
    image: "/placeholder.svg?height=400&width=600",
    type: "townhouse",
    beds: 3,
    baths: 2.5,
    sqft: 1600,
    amenities: ["Garden", "Fireplace", "Hardwood Floors", "Basement"],
    available: true,
    availableFrom: "2023-06-01",
    featured: false,
    coordinates: { lat: 42.3601, lng: -71.0589 },
    petFriendly: true,
    furnished: false,
    airConditioning: true,
    laundry: "In-unit",
    parking: "Street",
    yearBuilt: 1920,
  },
  {
    id: "6",
    title: "Modern Loft Apartment",
    description:
      "Stylish loft with high ceilings, exposed brick, and industrial design elements in a trendy neighborhood.",
    location: "Arts District, Portland",
    price: 2200,
    image: "/placeholder.svg?height=400&width=600",
    type: "loft",
    beds: 1,
    baths: 1,
    sqft: 950,
    amenities: ["Gym", "Rooftop", "Bike Storage", "Elevator"],
    available: true,
    availableFrom: "2023-05-15",
    featured: false,
    coordinates: { lat: 45.5051, lng: -122.675 },
    petFriendly: true,
    furnished: false,
    airConditioning: false,
    laundry: "In-building",
    parking: "Garage",
    yearBuilt: 2010,
  },
];

interface Property {
  id: string;
  title: string;
  description: string;
  location: string;
  price: number;
  image: string;
  type: string;
  beds: number;
  baths: number;
  sqft: number;
  amenities: string[];
  available: boolean;
  availableFrom: string;
  featured: boolean;
  coordinates: { lat: number; lng: number };
  petFriendly: boolean;
  furnished: boolean;
  airConditioning: boolean;
  laundry: string;
  parking: string;
  yearBuilt: number;
}

export default function PortfolioPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [filteredProperties, setFilteredProperties] = useState(properties);
  const [priceRange, setPriceRange] = useState([0, 6000]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>(
    []
  );
  const [bedroomsFilter, setBedroomsFilter] = useState("any");
  const [bathroomsFilter, setBathroomsFilter] = useState("any");
  const [sortOption, setSortOption] = useState("featured");
  const [showMap, setShowMap] = useState(true);
  const [compareProperties, setCompareProperties] = useState<typeof properties>(
    []
  );
  const [showComparison, setShowComparison] = useState(false);
  const [savedProperties, setSavedProperties] = useState<string[]>([]);

  // Filter properties based on selected filters
  useEffect(() => {
    let filtered = [...properties];

    // Filter by tab
    if (activeTab !== "all") {
      if (activeTab === "available") {
        filtered = filtered.filter((property) => property.available);
      } else if (activeTab === "featured") {
        filtered = filtered.filter((property) => property.featured);
      } else if (activeTab === "saved") {
        filtered = filtered.filter((property) =>
          savedProperties.includes(property.id)
        );
      }
    }

    // Filter by price range
    filtered = filtered.filter(
      (property) =>
        property.price >= priceRange[0] && property.price <= priceRange[1]
    );

    // Filter by property type
    if (selectedPropertyTypes.length > 0) {
      filtered = filtered.filter((property) =>
        selectedPropertyTypes.includes(property.type)
      );
    }

    // Filter by bedrooms
    if (bedroomsFilter !== "any") {
      const bedrooms = Number.parseInt(bedroomsFilter);
      if (bedroomsFilter === "4+") {
        filtered = filtered.filter((property) => property.beds >= 4);
      } else {
        filtered = filtered.filter((property) => property.beds === bedrooms);
      }
    }

    // Filter by bathrooms
    if (bathroomsFilter !== "any") {
      const bathrooms = Number.parseFloat(bathroomsFilter);
      if (bathroomsFilter === "3+") {
        filtered = filtered.filter((property) => property.baths >= 3);
      } else {
        filtered = filtered.filter((property) => property.baths === bathrooms);
      }
    }

    // Sort properties
    if (sortOption === "price-asc") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOption === "price-desc") {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortOption === "size-desc") {
      filtered.sort((a, b) => b.sqft - a.sqft);
    } else if (sortOption === "featured") {
      filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    setFilteredProperties(filtered);
  }, [
    activeTab,
    priceRange,
    selectedPropertyTypes,
    bedroomsFilter,
    bathroomsFilter,
    sortOption,
    savedProperties,
  ]);

  // Toggle property for comparison
  interface Property {
    id: string;
    title: string;
    description: string;
    location: string;
    price: number;
    image: string;
    type: string;
    beds: number;
    baths: number;
    sqft: number;
    amenities: string[];
    available: boolean;
    availableFrom: string;
    featured: boolean;
    coordinates: { lat: number; lng: number };
    petFriendly: boolean;
    furnished: boolean;
    airConditioning: boolean;
    laundry: string;
    parking: string;
    yearBuilt: number;
  }

  const togglePropertyComparison = (property: Property) => {
    if (compareProperties.some((p) => p.id === property.id)) {
      setCompareProperties(
        compareProperties.filter((p) => p.id !== property.id)
      );
    } else {
      if (compareProperties.length < 3) {
        setCompareProperties([...compareProperties, property]);
      }
    }
  };

  // Toggle saved property
  const toggleSavedProperty = (propertyId: string) => {
    if (savedProperties.includes(propertyId)) {
      setSavedProperties(savedProperties.filter((id) => id !== propertyId));
    } else {
      setSavedProperties([...savedProperties, propertyId]);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setActiveTab("all");
    setPriceRange([0, 6000]);
    setSelectedPropertyTypes([]);
    setBedroomsFilter("any");
    setBathroomsFilter("any");
    setSortOption("featured");
  };

  // Toggle property type selection
  const togglePropertyType = (type: string) => {
    if (selectedPropertyTypes.includes(type)) {
      setSelectedPropertyTypes(selectedPropertyTypes.filter((t) => t !== type));
    } else {
      setSelectedPropertyTypes([...selectedPropertyTypes, type]);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-4xl font-bold mb-4">Our Property Portfolio</h1>
        <p className="text-lg text-muted-foreground">
          Explore our diverse collection of properties across the country. Use
          the filters to find your perfect home.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar with filters */}
        <div className="w-full lg:w-64 space-y-6">
          <div className="flex lg:hidden justify-between items-center mb-4">
            <Button
              variant="outline"
              onClick={() => setShowMap(!showMap)}
              className="w-1/2"
            >
              {showMap ? "Hide Map" : "Show Map"}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-1/2 ml-2">
                  <Filter className="mr-2 h-4 w-4" />
                  Filters
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-4">
                  <h3 className="font-medium">Filters</h3>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Price Range</h4>
                    <Slider
                      defaultValue={priceRange}
                      max={6000}
                      min={0}
                      step={100}
                      onValueChange={setPriceRange}
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-sm">${priceRange[0]}</p>
                      <p className="text-sm">${priceRange[1]}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Property Type</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        "apartment",
                        "house",
                        "condo",
                        "townhouse",
                        "studio",
                        "loft",
                        "penthouse",
                      ].map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <Checkbox
                            id={`type-${type}-mobile`}
                            checked={selectedPropertyTypes.includes(type)}
                            onCheckedChange={() => togglePropertyType(type)}
                          />
                          <label
                            htmlFor={`type-${type}-mobile`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                          >
                            {type}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Bedrooms</label>
                      <Select
                        value={bedroomsFilter}
                        onValueChange={setBedroomsFilter}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="4+">4+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Bathrooms</label>
                      <Select
                        value={bathroomsFilter}
                        onValueChange={setBathroomsFilter}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="1.5">1.5</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="2.5">2.5</SelectItem>
                          <SelectItem value="3+">3+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={resetFilters}
                    variant="outline"
                    className="w-full"
                  >
                    Reset Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="hidden lg:block space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Price Range</h3>
              <Slider
                defaultValue={priceRange}
                max={6000}
                min={0}
                step={100}
                onValueChange={setPriceRange}
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm">${priceRange[0]}</p>
                <p className="text-sm">${priceRange[1]}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Property Type</h3>
              <div className="space-y-2">
                {[
                  "apartment",
                  "house",
                  "condo",
                  "townhouse",
                  "studio",
                  "loft",
                  "penthouse",
                ].map((type) => (
                  <div key={type} className="flex items-center space-x-2">
                    <Checkbox
                      id={`type-${type}`}
                      checked={selectedPropertyTypes.includes(type)}
                      onCheckedChange={() => togglePropertyType(type)}
                    />
                    <label
                      htmlFor={`type-${type}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                    >
                      {type}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Bedrooms</h3>
              <Select value={bedroomsFilter} onValueChange={setBedroomsFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4+">4+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Bathrooms</h3>
              <Select
                value={bathroomsFilter}
                onValueChange={setBathroomsFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="1.5">1.5</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="2.5">2.5</SelectItem>
                  <SelectItem value="3+">3+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={resetFilters} variant="outline" className="w-full">
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1">
          {/* Map section */}
          {showMap && (
            <div className="bg-muted rounded-lg h-[400px] mb-8 flex items-center justify-center">
              <div className="text-center">
                <p className="text-muted-foreground mb-2">Interactive Map</p>
                <p className="text-sm text-muted-foreground">
                  (In a real app, this would be an interactive map showing
                  property locations)
                </p>
              </div>
            </div>
          )}

          {/* Property listing section */}
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full sm:w-auto"
              >
                <TabsList>
                  <TabsTrigger value="all">All Properties</TabsTrigger>
                  <TabsTrigger value="available">Available Now</TabsTrigger>
                  <TabsTrigger value="featured">Featured</TabsTrigger>
                  <TabsTrigger value="saved">Saved</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Select value={sortOption} onValueChange={setSortOption}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="price-asc">
                      Price: Low to High
                    </SelectItem>
                    <SelectItem value="price-desc">
                      Price: High to Low
                    </SelectItem>
                    <SelectItem value="size-desc">Size: Largest</SelectItem>
                  </SelectContent>
                </Select>

                {compareProperties.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setShowComparison(true)}
                    className="whitespace-nowrap"
                  >
                    Compare ({compareProperties.length})
                  </Button>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Showing {filteredProperties.length} properties
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    onCompare={togglePropertyComparison}
                    isCompared={compareProperties.some(
                      (p) => p.id === property.id
                    )}
                    isSaved={savedProperties.includes(property.id)}
                    onSave={() => toggleSavedProperty(property.id)}
                  />
                ))}
              </div>

              {filteredProperties.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    No properties match your filters.
                  </p>
                  <Button
                    onClick={resetFilters}
                    variant="outline"
                    className="mt-4"
                  >
                    Reset Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Property comparison dialog */}
      <Dialog open={showComparison} onOpenChange={setShowComparison}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Property Comparison</DialogTitle>
            <DialogDescription>
              Compare features and amenities side by side
            </DialogDescription>
          </DialogHeader>

          {compareProperties.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Feature</TableHead>
                    {compareProperties.map((property) => (
                      <TableHead key={property.id}>
                        <div className="space-y-1">
                          <div className="font-medium">{property.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {property.location}
                          </div>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Price</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-price`}>
                        ${property.price}/mo
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Type</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell
                        key={`${property.id}-type`}
                        className="capitalize"
                      >
                        {property.type}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Bedrooms</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-beds`}>
                        {property.beds}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Bathrooms</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-baths`}>
                        {property.baths}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Square Feet</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-sqft`}>
                        {property.sqft}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Available From
                    </TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-available`}>
                        {property.available
                          ? property.availableFrom
                          : "Not available"}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Pet Friendly</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-pet`}>
                        {property.petFriendly ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Furnished</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-furnished`}>
                        {property.furnished ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">
                      Air Conditioning
                    </TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-ac`}>
                        {property.airConditioning ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-red-500" />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Laundry</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-laundry`}>
                        {property.laundry}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Parking</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-parking`}>
                        {property.parking}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Year Built</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell key={`${property.id}-year`}>
                        {property.yearBuilt}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Amenities</TableCell>
                    {compareProperties.map((property) => (
                      <TableCell
                        key={`${property.id}-amenities`}
                        className="max-w-[200px]"
                      >
                        <div className="flex flex-wrap gap-1">
                          {property.amenities.map((amenity) => (
                            <Badge
                              key={amenity}
                              variant="outline"
                              className="text-xs"
                            >
                              {amenity}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                Select properties to compare (up to 3)
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCompareProperties([])}
              disabled={compareProperties.length === 0}
            >
              Clear All
            </Button>
            <DialogClose asChild>
              <Button>Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface PropertyCardProps {
  property: Property;
  onCompare: (property: Property) => void;
  isCompared: boolean;
  isSaved: boolean;
  onSave: () => void;
}

function PropertyCard({
  property,
  onCompare,
  isCompared,
  isSaved,
  onSave,
}: PropertyCardProps) {
  const [showAvailability, setShowAvailability] = useState(false);

  return (
    <Card className="overflow-hidden h-full flex flex-col">
      <div className="relative">
        <div className="relative h-48 w-full overflow-hidden">
          <img
            src={property.image || "/placeholder.svg"}
            alt={property.title}
            className="object-cover transition-transform hover:scale-105"
          />
          {property.featured && (
            <Badge className="absolute top-2 left-2">Featured</Badge>
          )}
        </div>

        <div className="absolute top-2 right-2 flex gap-2">
          <Button
            variant="outline"
            size="icon"
            className={`bg-background ${isCompared ? "text-primary" : ""}`}
            onClick={() => onCompare(property)}
            title="Compare property"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className={`bg-background ${isSaved ? "text-primary" : ""}`}
            onClick={onSave}
            title={isSaved ? "Remove from saved" : "Save property"}
          >
            <Heart
              className="h-4 w-4"
              fill={isSaved ? "currentColor" : "none"}
            />
          </Button>
        </div>
      </div>

      <CardContent className="p-4 flex-grow">
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-lg line-clamp-1">
              {property.title}
            </h3>
            <div className="flex items-center text-muted-foreground">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="text-sm">{property.location}</span>
            </div>
          </div>

          <p className="text-sm line-clamp-2 text-muted-foreground">
            {property.description}
          </p>

          <div className="flex justify-between">
            <div className="flex items-center text-sm">
              <BedDouble className="h-4 w-4 mr-1" />
              <span>
                {property.beds} {property.beds === 1 ? "Bed" : "Beds"}
              </span>
            </div>
            <div className="flex items-center text-sm">
              <Bath className="h-4 w-4 mr-1" />
              <span>
                {property.baths} {property.baths === 1 ? "Bath" : "Baths"}
              </span>
            </div>
            <div className="flex items-center text-sm">
              <Ruler className="h-4 w-4 mr-1" />
              <span>{property.sqft} sqft</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 mt-2">
            {property.amenities
              .slice(0, 3)
              .map((amenity: boolean | Key | undefined) => (
                <Badge
                  key={String(amenity)}
                  variant="outline"
                  className="text-xs"
                >
                  {amenity}
                </Badge>
              ))}
            {property.amenities.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{property.amenities.length - 3} more
              </Badge>
            )}
          </div>
        </div>
      </CardContent>

      <CardFooter className="p-4 border-t">
        <div className="flex justify-between items-center w-full">
          <div className="font-bold text-lg">${property.price}/mo</div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAvailability(true)}
            >
              <Calendar className="h-4 w-4 mr-1" />
              Availability
            </Button>
            <Link to={`/properties/${property.id}`}>
              <Button size="sm">View Details</Button>
            </Link>
          </div>
        </div>
      </CardFooter>
      <Dialog open={showAvailability} onOpenChange={setShowAvailability}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Availability Calendar</DialogTitle>
            <DialogDescription>
              Check availability for {property.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-muted-foreground">
                (In a real app, this would be an interactive calendar)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`viewing-date-${property.id}`}>
                Select a date for viewing
              </Label>
              <Input id={`viewing-date-${property.id}`} type="date" />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`viewing-time-${property.id}`}>
                Select a time
              </Label>
              <Select>
                <SelectTrigger id={`viewing-time-${property.id}`}>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10:00">10:00 AM</SelectItem>
                  <SelectItem value="11:00">11:00 AM</SelectItem>
                  <SelectItem value="12:00">12:00 PM</SelectItem>
                  <SelectItem value="13:00">1:00 PM</SelectItem>
                  <SelectItem value="14:00">2:00 PM</SelectItem>
                  <SelectItem value="15:00">3:00 PM</SelectItem>
                  <SelectItem value="16:00">4:00 PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button>Schedule Viewing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
